import { Component, OnInit } from '@angular/core';
import { FeedbackSurveyService } from '../services/survey-tools.service';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { takeUntil, switchMap, tap, filter } from 'rxjs/operators';
import { Subject, Observable, of, combineLatest } from 'rxjs';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { ReviewsService } from '../reviews.service';

@Component({
  selector: 'app-survey-tool',
  templateUrl: './survey-tool.component.html',
  styleUrls: ['./survey-tool.component.scss'],
})
export class SurveyToolComponent implements OnInit {
  private static readonly NON_DATA_COLUMN_KEYS = new Set([
    'responded_to',
    'internal_user_comment',
    'responded_by',
    'record_type',
    'type',
  ]);
  private ngUnsubscribe$: Subject<any> = new Subject();
  private userLoginId: number;
  private accountId: number;
  private currentAccountIdent: string = '';
  private currentPageNumber: number;
  public currentUser: any;

  public actionItems = [
    {
      label: 'CSV (All Records)',
      command: () => {
        this.exportCSVAllRecords('csv');
      },
    },
    {
      label: 'XLSX (All Records)',
      command: () => {
        this.exportXLSXAllRecords();
      },
    },
    {
      label: 'CSV (Current Page)',
      command: () => {
        this.exportCSVCurrentPage('csv');
      },
    },
    {
      label: 'XLSX (Current Page)',
      command: () => {
        this.exportXLSXCurrentPage();
      },
    },
  ];
  // Rows on the current table page (slice of allRecordsCache).
  public feedbackSurveysList: any[] = [];
  public isLoadingData = false;
  public statisticsList: any[] = [];
  public displayColumns: Array<{ key: string; label: string; sortField: string | null }> = [];
  public first: number = 0;
  public rows: number = 25;
  public totalRecords: number = 0;
  // Active p-table sort column (date or updated_at).
  public tableSortField: string | null = null;
  public tableSortOrder: number = -1;
  public showEditDialog: boolean = false;
  public selectedSurvey: any = null;
  public editInternalComment: string = '';
  public editRespondedTo: boolean = false;
  public isSavingResponse: boolean = false;
  public isExporting: boolean = false;
  // Full survey list for client paging, sort, and export-all.
  private allRecordsCache: any[] | null = null;

  constructor(
    private feedbackSurveyService: FeedbackSurveyService,
    private sessionService: SessionService,
    private notifyService: NotifyService,
    private reviewsService: ReviewsService
  ) {}

  ngOnInit(): void {
    this.subscribeAccountAndUser();
  }

  /**
   * @description Reloads surveys when account and user are both available.
   * Clears allRecordsCache and sort state when account or user changes.
   * @returns None.
   * @throws None.
   */
  private subscribeAccountAndUser(): void {
    combineLatest([
      this.sessionService.getSelectedAccount$(),
      this.sessionService.getCurrentUser$(),
    ])
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter(([account, user]) => !!account?._id && !!user?.login?._id),
      )
      .subscribe(([account, user]) => {
        this.accountId = account._id;
        this.currentAccountIdent = account.ident || '';
        this.userLoginId = user.login._id;
        this.currentUser = user;
        this.allRecordsCache = null;
        this.tableSortField = null;
        this.tableSortOrder = -1;
        this.loadAllRecords();
      });
  }

  /**
   * @description Fetches all surveys, builds columns, and applies default newest-first date sort.
   * Shows page one; no-op while isLoadingData is already true.
   * @returns None.
   * @throws None. Fetch errors are handled in the subscribe error callback.
   */
  private loadAllRecords(): void {
    if (this.isLoadingData) {
      return;
    }
    const accountIdAtFetchTime = this.accountId;
    this.isLoadingData = true;
    this.feedbackSurveysList = [];
    this.fetchAllSurveyRecords()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (records) => {
          if (this.accountId !== accountIdAtFetchTime) {
            this.isLoadingData = false;
            this.loadAllRecords();
            return;
          }
          this.displayColumns = this.buildDisplayColumns(records);
          const dateField = this.displayColumns.find(c => c.sortField)?.sortField;
          if (dateField) {
            records = records.slice().sort((a, b) => {
              const ta = new Date(a[dateField] ?? '').getTime() || 0;
              const tb = new Date(b[dateField] ?? '').getTime() || 0;
              return tb - ta;
            });
            this.allRecordsCache = records;
            this.tableSortField = dateField;
            this.tableSortOrder = -1;
          }
          this.totalRecords = records.length;
          this.feedbackSurveysList = records.slice(0, this.rows);
          this.statisticsList = this.computeSummary(records);
          this.first = 0;
          this.currentPageNumber = 1;
          this.isLoadingData = false;
        },
        error: () => {
          this.isLoadingData = false;
          if (this.accountId !== accountIdAtFetchTime) {
            this.loadAllRecords();
            return;
          }
          this.notifyService.info('Error while fetching feedback surveys.');
        },
      });
  }

  /**
   * @description Builds rating totals and averages for the stats panel above the table.
   * @param records Full survey list used for aggregates.
   * @returns One summary object in an array, or [] when records is empty.
   * @throws None.
   */
  private computeSummary(records: any[]): any[] {
    if (!records.length) {
      return [];
    }
    const ratings = records.map(r => Number(r.rating)).filter(r => !isNaN(r) && r > 0);
    const total = ratings.length;
    const avgRating = total ? ratings.reduce((a, b) => a + b, 0) / total : 0;
    const dates = records.map(r => r.date).filter(Boolean).sort();
    return [{
      account_ident: this.currentAccountIdent,
      period_start_date: dates[0]?.substring(0, 10) ?? '',
      directory: 'survey',
      Average_rating: avgRating.toFixed(2),
      Average_rating__change: '',
      Total_surveys: records.length,
      Total_ratings: total,
      Total_ratings__change: '',
      Total_positive_ratings: ratings.filter(r => r >= 4).length,
      Total_neutral_ratings: ratings.filter(r => r === 3).length,
      Total_negative_ratings: ratings.filter(r => r <= 2).length,
    }];
  }

  /**
   * @description Returns all formatted survey rows, using allRecordsCache when present.
   * Otherwise follows API cursor pages until exhausted and caches the result.
   * @returns Observable that emits one flat array of survey records.
   * @throws None. HTTP errors propagate to subscribers.
   */
  private fetchAllSurveyRecords(): Observable<any[]> {
    if (this.allRecordsCache) {
      return of(this.allRecordsCache);
    }

    const accountIdAtFetchTime = this.accountId;
    const userLoginIdAtFetchTime = this.userLoginId;

    const fetchChunk = (cursor?: string, accumulated: any[] = []): Observable<any[]> => {
      return this.feedbackSurveyService.getFeedbackSurveysList(accountIdAtFetchTime, userLoginIdAtFetchTime, cursor).pipe(
        switchMap((res: any) => {
          const records: any[] = res?.records ?? [];
          const all = accumulated.concat(this.feedbackSurveyService.formatFeedbackSurveyData(records));
          const nextCursor: string | null = res?.exclusive_start_key ?? null;
          return nextCursor ? fetchChunk(nextCursor, all) : of(all);
        })
      );
    };

    return fetchChunk().pipe(
      tap((records: any[]) => {
        if (this.accountId === accountIdAtFetchTime) {
          this.allRecordsCache = records;
        }
      })
    );
  }

  /**
   * @description Exports every survey row as CSV in the current table sort order.
   * @param format Format identifier passed to the CSV report utility.
   * @returns None.
   * @throws None. Export and fetch errors are handled via subscribe callbacks.
   */
  exportCSVAllRecords(format: string): void {
    this.isExporting = true;
    const accountIdent = this.currentAccountIdent;
    this.fetchAllSurveyRecords().subscribe({
      next: (records) => {
        this.reviewsService.genCSVReport(this.applyCurrentSort(records), format, `feedback-survey-all-${accountIdent}`);
      },
      error: () => { this.isExporting = false; },
      complete: () => { this.isExporting = false; },
    });
  }

  /**
   * @description Exports every survey row as XLSX in the current table sort order.
   * Notifies when no rows are available.
   * @returns None.
   * @throws None. Export and fetch errors are handled via subscribe callbacks.
   */
  exportXLSXAllRecords(): void {
    this.isExporting = true;
    const accountIdent = this.currentAccountIdent;
    this.fetchAllSurveyRecords().subscribe({
      next: (records) => {
        if (!records.length) {
          this.notifyService.info('No survey records available to export.');
          return;
        }
        const sorted = this.applyCurrentSort(records);
        const headers = Object.keys(sorted[0]);
        const tableData = sorted.map(survey => Object.values(survey));
        const xlslDataArray = [headers, ...tableData];
        this.reviewsService.exportAsExcelFile(xlslDataArray, `feedback-survey-all-${accountIdent}`);
      },
      error: () => { this.isExporting = false; },
      complete: () => { this.isExporting = false; },
    });
  }


  /**
   * @description Exports feedbackSurveysList (current table page) as CSV.
   * @param format Format identifier passed to the CSV report utility.
   * @returns None.
   * @throws None.
   */
  exportCSVCurrentPage(format: string): void {
    this.reviewsService.genCSVReport(this.feedbackSurveysList, format, 'feedback-survey-page-' + this.currentPageNumber + '-' + this.currentAccountIdent);
  }

  /**
   * @description Exports feedbackSurveysList (current table page) as XLSX.
   * Notifies when the current page has no rows.
   * @returns None.
   * @throws None.
   */
  exportXLSXCurrentPage(): void {
    if (!this.feedbackSurveysList.length) {
      this.notifyService.info('No survey records available to export.');
      return;
    }

    const headers = Object.keys(this.feedbackSurveysList[0]);
    const tableData = this.feedbackSurveysList.map((survey) => Object.values(survey));
    const xlslDataArray = [headers, ...tableData];
    this.reviewsService.exportAsExcelFile(xlslDataArray, 'feedback-survey-page-' + this.currentPageNumber + '-' + this.currentAccountIdent);
  }

  /**
   * @description Syncs paginator first and rows from a PrimeNG page event.
   * @param event Paginator event with first and rows.
   * @returns None.
   * @throws None.
   */
  public pageChange(event): void {
    this.first = event.first;
    this.rows = event.rows;
  }

  /**
   * @description Pages and sorts client-side from allRecordsCache.
   * Updates tableSortField only when event.sortField is set so pagination does not clear sort.
   * @param event PrimeNG lazy-load event (first, rows, sortField, sortOrder).
   * @returns None.
   * @throws None.
   */
  public lazyLoadLocations(event): void {
    this.first = event.first;
    this.rows = event.rows;
    this.currentPageNumber = event.first / this.rows + 1;
    if (!this.allRecordsCache) {
      if (!this.isLoadingData) {
        this.loadAllRecords();
      }
      return;
    }

    if (event.sortField) {
      this.tableSortField = event.sortField;
      this.tableSortOrder = event.sortOrder;
    }
    this.feedbackSurveysList = this.applyCurrentSort(this.allRecordsCache).slice(event.first, event.first + event.rows);
  }

  /**
   * @description Sorts a copy by tableSortField/tableSortOrder (date values).
   * Returns records unchanged when no sort column is active.
   * @param records Survey rows to sort (usually allRecordsCache).
   * @returns Sorted copy, or the original array reference when tableSortField is null.
   * @throws None.
   */
  private applyCurrentSort(records: any[]): any[] {
    if (!this.tableSortField) {
      return records;
    }
    const field = this.tableSortField;
    const order = this.tableSortOrder;
    return [...records].sort((a, b) => {
      const ta = new Date(a[field] ?? '').getTime() || 0;
      const tb = new Date(b[field] ?? '').getTime() || 0;
      return (ta - tb) * order;
    });
  }

  /**
   * @description Builds table column metadata from record keys in first-seen order.
   * Skips response-edit helper keys; marks date and updated_at as sortable.
   * @param records Formatted survey rows from the API.
   * @returns Column definitions with key, label, and sortField (or null).
   * @throws None.
   */
  private buildDisplayColumns(records: any[]): Array<{ key: string; label: string; sortField: string | null }> {
    if (!Array.isArray(records) || !records.length) {
      return [];
    }

    const orderedKeys = records.reduce((keys: string[], record: Record<string, any>) => {
      Object.keys(record || {}).forEach((key) => {
        if (!SurveyToolComponent.NON_DATA_COLUMN_KEYS.has(key) && !keys.includes(key)) {
          keys.push(key);
        }
      });
      return keys;
    }, []);

    return orderedKeys.map((key) => ({
      key,
      label: this.formatColumnLabel(key),
      sortField: this.getSortFieldForColumn(key),
    }));
  }

  /**
   * @description Converts an API field key to a readable table header label.
   * @param key Raw property key from a survey record.
   * @returns Title-case label, or '' when key is empty.
   * @throws None.
   */
  private formatColumnLabel(key: string): string {
    if (!key) {
      return '';
    }

    const expanded = key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();

    return expanded
      .split(/\s+/)
      .map((word) => {
        if (word.toUpperCase() === 'ID') {
          return 'ID';
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  /**
   * @description Resolves the p-table sort field for a column key.
   * @param key Column key from displayColumns.
   * @returns The same key for date/updated_at columns, otherwise null.
   * @throws None.
   */
  private getSortFieldForColumn(key: string): string | null {
    if (!key) {
      return null;
    }

    const normalizedKey = key.toLowerCase();
    if (normalizedKey === 'date' || normalizedKey === 'updated_at' || normalizedKey === 'updatedat') {
      return key;
    }

    return null;
  }

  /**
   * @description ngClass map for fixed-width header cells (type, date, comment).
   * @param column Column metadata from displayColumns.
   * @returns Class name flags for ngClass.
   * @throws None.
   */
  public getColumnHeaderClasses(column: { key: string }): Record<string, boolean> {
    return {
      'col-date': column.key === 'date',
      'col-comment': column.key === 'comment',
    };
  }

  /**
   * @description ngClass map for fixed-width body cells (type, date, comment).
   * @param column Column metadata from displayColumns.
   * @returns Class name flags for ngClass.
   * @throws None.
   */
  public getColumnCellClasses(column: { key: string }): Record<string, boolean> {
    return {
      'col-date': column.key === 'date',
      'col-comment': column.key === 'comment',
    };
  }

  /**
   * @description Normalizes a cell value for display (empty to N/A, arrays joined, objects JSON).
   * @param record Survey row rendered in the table.
   * @param key Column property key on the record.
   * @returns Display-safe primitive for the template.
   * @throws None.
   */
  public getDisplayCellValue(record: Record<string, any>, key: string): string | number | boolean {
    const value = record?.[key];

    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'string' && value.trim() === '') {
      return 'N/A';
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  }

  /**
   * @description Opens the response edit dialog and loads draft fields from the row.
   * @param survey Selected survey row.
   * @returns None.
   * @throws None.
   */
  public openEditDialog(survey: any): void {
    this.selectedSurvey = survey;
    this.editInternalComment = survey?.internal_user_comment || '';
    this.editRespondedTo = survey?.responded_to ?? false;
    this.showEditDialog = true;
  }

  /**
   * @description Closes the edit dialog and clears selection and draft fields.
   * @returns None.
   * @throws None.
   */
  public closeEditDialog(): void {
    this.showEditDialog = false;
    this.selectedSurvey = null;
    this.editInternalComment = '';
    this.editRespondedTo = false;
  }

  /**
   * @description Builds responded_by payload from the session user for updateSurveyResponse.
   * @returns { user_id, name, email_id } or null when login is unavailable.
   * @throws None.
   */
  public getRespondedByValue(): { user_id: number; name: string; email_id: string } | null {
    if (!this.currentUser?.login) {
      return null;
    }
    const user_id = this.currentUser.login._id;
    const name = this.currentUser.login.note ?? this.currentUser.login.name ?? '';
    const email_id = this.currentUser.login.loginId ?? this.currentUser.email_id ?? '';
    return { user_id, name, email_id };
  }

  /**
   * @description Display label for responded by in the table and dialog.
   * @returns "Name (email)", name only, or N/A when getRespondedByValue() is null.
   * @throws None.
   */
  public getRespondedByDisplayText(): string {
    const obj = this.getRespondedByValue();
    if (!obj) {
      return 'N/A';
    }
    return obj.email_id ? `${obj.name} (${obj.email_id})` : obj.name || 'N/A';
  }

  /**
   * @description PATCHes responded_to and internal_user_comment for the selected row.
   * Clears allRecordsCache on success; no-op when selection or required fields are missing.
   * @returns None.
   * @throws None. API errors are handled in the subscribe error callback.
   */
  public saveResponseFields(): void {
    if (!this.selectedSurvey) {
      return;
    }
    const accountIdent = this.selectedSurvey.account_ident || this.currentAccountIdent;
    const type = this.selectedSurvey.record_type;
    if (!accountIdent || !type || type === 'N/A') {
      this.notifyService.info('Missing account or response type; cannot update.');
      return;
    }
    const respondedBy = this.getRespondedByValue();
    const body = {
      account_ident: accountIdent,
      type,
      updates: {
        responded_to: this.editRespondedTo,
        internal_user_comment: this.editInternalComment,
        ...(respondedBy && { responded_by: respondedBy }),
      },
    };
    console.log('Survey update API request body:', body);
    this.isSavingResponse = true;
    this.feedbackSurveyService
      .updateSurveyResponse(this.accountId, this.userLoginId, body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        () => {
          this.selectedSurvey.responded_to = this.editRespondedTo;
          this.selectedSurvey.internal_user_comment = this.editInternalComment;
          this.selectedSurvey.responded_by = this.getRespondedByDisplayText();
          this.allRecordsCache = null;
          this.notifyService.success('Response updated successfully.');
          this.closeEditDialog();
        },
        () => {
          this.notifyService.info('Error while updating response.');
        },
        () => {
          this.isSavingResponse = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
